import { AsyncPipe } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, isDevMode, NgZone, OnInit } from "@angular/core";
import { NewUiButton } from "@ankaadia/ankaadia-shared-frontend";
import { Document, DocumentMode, StaticDataType } from "@ankaadia/graphql";
import { TranslocoService } from "@jsverse/transloco";
import { FieldType, FormlyFieldConfig, FormlyModule } from "@ngx-formly/core";
import { pick } from "lodash";
import { Accordion, AccordionContent, AccordionHeader, AccordionPanel } from "primeng/accordion";
import { MenuItem } from "primeng/api";
import { Menu } from "primeng/menu";
import { Tooltip } from "primeng/tooltip";
import { debounceTime, distinctUntilChanged, map, merge, Observable, of, startWith, switchMap } from "rxjs";
import { DocumentsService } from "../../features/documents/documents.service";
import { CandidateProfileDialogService } from "../../organization-specific/base-candidate-profile/candidate-profile-dialog.service";
import { getFormErrorPaths, hasFormErrors, isFilledByAI, isSuggested } from "../../shared/functions/form-errors";
import { AccordionRememberPanelDirective } from "../../shared/primeng/accordion-remember-panel/accordion-remember-panel.directive";
import { outsideZone } from "../../shared/rxjs/outside-zone";
import { SettingsService } from "../../shared/services/settings.service";
import { StaticDataService } from "../../shared/static-data/static-data.service";

@Component({
  selector: "new-ui-formly-tabs",
  templateUrl: "./new-ui-formly-tabs.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormlyModule,
    AsyncPipe,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    NewUiButton,
    Menu,
    AccordionRememberPanelDirective,
    Tooltip,
  ],
})
export class NewUiFormlyTabsComponent extends FieldType implements OnInit {
  private readonly language = this.transloco.getActiveLang();

  protected readonly showDebugInfo = isDevMode() && this.settingsService.isMasterUser;
  protected invalid$: Observable<boolean[]>;
  protected suggested$: Observable<boolean[]>;
  protected filledByAI$: Observable<boolean[]>;
  protected uploadMenu: MenuItem[];

  protected tabErrorsTooltip(index: number): string | null {
    const tab = this.field.fieldGroup?.[index];
    if (!tab?.formControl) return null;
    const paths = getFormErrorPaths(tab.formControl);
    return paths.length ? paths.join("<br>") : null;
  }

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly staticDataService: StaticDataService,
    private readonly transloco: TranslocoService,
    private readonly documentService: DocumentsService,
    private readonly profileDialogService: CandidateProfileDialogService,
    private readonly ngZone: NgZone,
    private readonly settingsService: SettingsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.invalid$ = this.getInvalid();
    this.suggested$ = this.getSuggested();
    this.filledByAI$ = this.getFilledByAI();
    this.setUploadMenu();
  }

  protected openUploadDialog(documentType: string): void {
    this.documentService
      .getByType(documentType, this.formState.organizationId, this.formState.candidateId)
      .pipe(
        switchMap((document) => (document ? of(document) : this.createDocument(documentType))),
        map((document) => ({
          document,
          candidateForm: this.form,
          showDocumentCompletion: this.props.showDocumentCompletion,
        }))
      )
      .subscribe((event) => this.profileDialogService.documentDialog.next(event));
  }

  private createDocument(type: string): Observable<Document> {
    return this.documentService.createEmptyDocument(
      DocumentMode.Candidate,
      this.formState.organizationId,
      this.formState.candidateId,
      null,
      type
    );
  }

  private setUploadMenu(): void {
    if (!this.props.uploadTypes) return;

    if (this.formState.candidateId) {
      this.staticDataService
        .getStaticData(
          StaticDataType.AllowedUploadFileTypes,
          this.language,
          pick(this.formState, "candidateId", "organizationId")
        )
        .subscribe((data) => {
          this.uploadMenu = this.props.uploadTypes?.map((documentType) => ({
            label: data.find((d) => d.value == documentType)?.label,
            command: (): void => this.openUploadDialog(documentType),
          }));
          this.changeDetector.markForCheck();
        });
    } else {
      this.uploadMenu = null;
    }
  }

  private getInvalid(): Observable<boolean[]> {
    return merge(this.options.fieldChanges, this.form.statusChanges).pipe(
      startWith(undefined),
      debounceTime(100),
      map(() => this.calculateInvalidity()),
      distinctUntilChanged(),
      outsideZone(this.ngZone)
    );
  }

  private calculateInvalidity(): boolean[] {
    const func = (field: FormlyFieldConfig): boolean => {
      if (field.hide) return false;
      return field.fieldGroup?.length && !field.fieldArray
        ? field.fieldGroup.some((f) => func(f))
        : hasFormErrors(field.formControl);
    };

    return this.field.fieldGroup.map((field) => func(field));
  }

  private getSuggested(): Observable<boolean[]> {
    return merge(this.options.fieldChanges, this.form.statusChanges).pipe(
      startWith(undefined),
      debounceTime(100),
      map(() => this.calculateSuggested()),
      distinctUntilChanged(),
      outsideZone(this.ngZone)
    );
  }

  private calculateSuggested(): boolean[] {
    const func = (field: FormlyFieldConfig): boolean => {
      if (field.hide) return false;
      return field.fieldGroup?.length && !field.fieldArray
        ? field.fieldGroup.some((f) => func(f))
        : isSuggested(field.formControl);
    };

    return this.field.fieldGroup.map((field) => func(field));
  }

  private getFilledByAI(): Observable<boolean[]> {
    return merge(this.options.fieldChanges, this.form.statusChanges).pipe(
      startWith(undefined),
      debounceTime(100),
      map(() => this.calculateFilledByAI()),
      distinctUntilChanged(),
      outsideZone(this.ngZone)
    );
  }

  private calculateFilledByAI(): boolean[] {
    const invalid = this.calculateInvalidity();
    const func = (field: FormlyFieldConfig): boolean =>
      field.fieldGroup?.length && !field.fieldArray
        ? field.fieldGroup.some((f) => func(f))
        : isFilledByAI(field.formControl);

    return this.field.fieldGroup.map((field, i) => !invalid[i] && func(field));
  }
}
